import { CheckCircle2, SearchX, ShieldAlert } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router';
import { StateMessage } from '@/admin-area/ui/StateMessage';
import { ListToolbar } from '@/components/list/ListToolbar';
import { PageSizeSelect } from '@/components/list/PageSizeSelect';
import { useListParams } from '@/components/list/use-list-params';
import { Button } from '@/components/ui/button';
import type { FindingKind, OverviewItem } from '../api-types';
import { FindingCard } from './FindingCard';
import { narrow, pageOf, rowsOf } from './findings-view';

const KINDS: readonly FindingKind[] = [
  'misplaced',
  'bad-name',
  'unknown-code',
  'unknown-type',
  'draft-not-next-to-final',
  'obsolete-not-in-folder',
  'patient-data-suspected',
];

/** The patient-data notice: calm words, and «Mostra» narrows the list to those findings. */
function PatientNotice({ count, onShow }: { readonly count: number; readonly onShow: () => void }) {
  const { t } = useTranslation('archive');
  return (
    <div
      role="status"
      className="bg-[var(--blue-tint)] border-[var(--blue-tint-border)] text-foreground flex flex-wrap items-center gap-3 rounded-md border p-4"
    >
      <ShieldAlert aria-hidden="true" className="size-5 shrink-0" />
      <div className="min-w-0 flex-1">
        <p className="font-semibold">{t('overview.patient.title', { count })}</p>
        <p className="text-sm">{t('overview.patient.text')}</p>
      </div>
      <Button variant="outline" onClick={onShow}>
        {t('overview.patient.show')}
      </Button>
    </div>
  );
}

/** The findings of the folder in view: search, kind filter, numbered pages (10/25/50), U6. */
export function FindingsPanel({
  items,
  name,
}: {
  readonly items: readonly OverviewItem[];
  readonly name: string | undefined;
}) {
  const { t } = useTranslation('archive');
  const list = useListParams(['kind']);
  const all = rowsOf(items);
  const kind = list.active.kind ?? null;
  const shown = narrow(all, list.q, kind);
  const page = pageOf(shown, Number(list.params.get('page') ?? '1') || 1, list.pageSize);
  const patientCount = all.filter((row) => row.finding.kind === 'patient-data-suspected').length;
  const narrowed = list.q !== '' || kind !== null;
  // One write per change: react-router's functional updates do not chain within one event.
  const [, setParams] = useSearchParams();
  const write = (changes: Record<string, string | null>) =>
    setParams(
      (current) => {
        const next = new URLSearchParams(current);
        for (const [key, value] of Object.entries(changes)) {
          if (value === null || value === '') next.delete(key);
          else next.set(key, value);
        }
        return next;
      },
      { replace: true },
    );

  return (
    <section
      aria-label={t('overview.findingsOf', { name: name ?? t('overview.root') })}
      className="flex flex-col gap-4"
    >
      {patientCount > 0 ? (
        <PatientNotice
          count={patientCount}
          onShow={() => write({ kind: 'patient-data-suspected', page: null })}
        />
      ) : null}
      {all.length > 0 ? (
        <ListToolbar
          label={t('overview.findingsOf', { name: name ?? t('overview.root') })}
          search={{
            label: t('overview.search'),
            value: list.q,
            onWrite: (q) => write({ q, page: null }),
          }}
          fields={[
            {
              key: 'kind',
              label: t('overview.kind'),
              options: KINDS.map((value) => ({ value, label: t(`overview.kinds.${value}`) })),
            },
          ]}
          active={list.active}
          onFilter={(key, value) => write({ [key]: value, page: null })}
          onClear={() => write({ q: null, kind: null, page: null })}
        />
      ) : null}
      {all.length === 0 ? (
        <StateMessage
          icon={CheckCircle2}
          title={t('overview.allInLine')}
          text={t('overview.allInLineText')}
        />
      ) : null}
      {all.length > 0 && shown.length === 0 && narrowed ? (
        <StateMessage
          icon={SearchX}
          title={t('overview.noResults')}
          action={
            <Button variant="outline" onClick={() => write({ q: null, kind: null, page: null })}>
              {t('overview.clear')}
            </Button>
          }
        />
      ) : null}
      {page.rows.length > 0 ? (
        <>
          <ul className="m-0 list-none p-0">
            {page.rows.map((row) => (
              <FindingCard key={`${row.item.id}:${row.finding.id}`} row={row} />
            ))}
          </ul>
          <div className="flex flex-wrap items-center justify-between gap-3 text-sm">
            <span>{t('overview.findingCount', { count: shown.length })}</span>
            <nav
              aria-label={t('overview.page', { page: page.page, pages: page.pages })}
              className="flex items-center gap-2"
            >
              <Button
                variant="outline"
                disabled={page.page <= 1}
                onClick={() => write({ page: String(page.page - 1) })}
              >
                {t('overview.previous')}
              </Button>
              <span>{t('overview.page', { page: page.page, pages: page.pages })}</span>
              <Button
                variant="outline"
                disabled={page.page >= page.pages}
                onClick={() => write({ page: String(page.page + 1) })}
              >
                {t('overview.next')}
              </Button>
            </nav>
            <PageSizeSelect
              value={list.pageSize}
              onChange={(size) =>
                write({ pageSize: size === 50 ? null : String(size), page: null })
              }
            />
          </div>
        </>
      ) : null}
    </section>
  );
}
